import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Card, { CARD_SHADOW } from '../../core/Card';
import Icon from '../../core/Icon';
import {
    FLOW_CARD_CONNECTOR_ICON,
    FLOW_CARD_VARIANT,
    FLOW_CARD_VARIANTS,
} from './constants';

// Composite — FlowCard
// One card in a vertical flow: a head that names the thing (icon and `title`), says what
// kind it is (`type`), and carries its settings and actions on the right (`meta`,
// `actions`); and a `body` beneath for what is configured on it. Every slot is a node the
// caller renders, so the card knows nothing about steps, outcomes or automations — it owns
// the surface, the column rhythm and the drag/drop states. `FlowCard.Connector` is the arrow
// drawn between two cards.
// Styles: ./index.less ships via less/ats_style.less (white-label pipeline).

const FlowCard = ({
    icon,
    title,
    type,
    meta,
    actions,
    body,
    dragHandle,
    variant = FLOW_CARD_VARIANT.STEP,
    dragging = false,
    dropTarget = false,
    disabled = false,
    className,
    children,
    ...rest
}) => (
    <Card
        shadow={CARD_SHADOW.NONE}
        className={classNames(
            'ds-flow-card',
            `ds-flow-card--${FLOW_CARD_VARIANTS.includes(variant) ? variant : FLOW_CARD_VARIANT.STEP}`,
            {
                'ds-flow-card--dragging': dragging,
                'ds-flow-card--drop-target': dropTarget,
                'ds-flow-card--disabled': disabled,
            },
            className,
        )}
        {...rest}
    >
        {dragHandle}
        <div className="ds-flow-card__head">
            <div className="ds-flow-card__identity">
                {icon && <span className="ds-flow-card__icon">{icon}</span>}
                <span className="ds-flow-card__title">{title}</span>
            </div>
            {type != null && type !== '' && <div className="ds-flow-card__type">{type}</div>}
            {(meta || actions) && (
                <div className="ds-flow-card__meta">
                    {meta && <div className="ds-flow-card__meta-items">{meta}</div>}
                    {actions && <div className="ds-flow-card__actions">{actions}</div>}
                </div>
            )}
        </div>
        {(body || children) && (
            <div className="ds-flow-card__body">
                {body}
                {children}
            </div>
        )}
    </Card>
);

// The arrow from one card to the next.
FlowCard.Connector = ({ className, label }) => (
    <div className={classNames('ds-flow-card-connector', className)} aria-hidden={label ? undefined : 'true'}>
        <Icon value={FLOW_CARD_CONNECTOR_ICON} noMargin title={label} />
    </div>
);

FlowCard.Connector.propTypes = {
    className: PropTypes.string,
    /** Names the connector for assistive tech; without it the arrow is decorative */
    label: PropTypes.string,
};

FlowCard.propTypes = {
    /** Leading glyph — an Icon, or any node the caller renders */
    icon: PropTypes.node,
    /** What this card is: a name, or an input that edits it */
    title: PropTypes.node,
    /** The kind of thing it is: a label, or a control that changes it */
    type: PropTypes.node,
    /** Settings shown on the right — typically MetaButtons */
    meta: PropTypes.node,
    /** Controls at the far right — remove, toggles */
    actions: PropTypes.node,
    /** What is configured on this card, under the head */
    body: PropTypes.node,
    /** A DragHandle, placed in the card's margin */
    dragHandle: PropTypes.node,
    /** `step` carries the type column; `outcome` is the end-state card */
    variant: PropTypes.oneOf(FLOW_CARD_VARIANTS),
    /** This card is the one being dragged */
    dragging: PropTypes.bool,
    /** A dragged card would drop here */
    dropTarget: PropTypes.bool,
    /** Greys the surface — nothing on it can be changed */
    disabled: PropTypes.bool,
    className: PropTypes.string,
    /** Alternative to `body` for callers that prefer children */
    children: PropTypes.node,
};

export { FLOW_CARD_VARIANT, FLOW_CARD_VARIANTS };
export default FlowCard;
